import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface SharedContent {
  title: string;
  content_html: string;
  meta_description: string;
  word_count: number;
  shared_by: string;
  content_type: string;
  created_at?: string;
  noindex: boolean;
}

interface ValidationResult {
  valid: boolean;
  reason?: string;
  content_title?: string;
  shared_by?: string;
}

export default function SharedContentViewerPage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [content, setContent] = useState<SharedContent | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Set noindex meta tag
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);

    const metaGoogle = document.createElement('meta');
    metaGoogle.name = 'googlebot';
    metaGoogle.content = 'noindex';
    document.head.appendChild(metaGoogle);

    return () => {
      document.head.removeChild(meta);
      document.head.removeChild(metaGoogle);
    };
  }, []);

  // Validate share token first (no auth needed)
  useEffect(() => {
    if (!shareToken) return;
    fetch(`/api/shares/validate/${shareToken}`)
      .then(r => r.json())
      .then(data => {
        setValidation(data);
        if (!data.valid) {
          setLoading(false);
        }
      })
      .catch(() => {
        setValidation({ valid: false, reason: 'error' });
        setLoading(false);
      });
  }, [shareToken]);

  // Fetch content if authenticated and share is valid
  useEffect(() => {
    if (authLoading || !validation) return;
    if (!validation.valid) { setLoading(false); return; }
    if (!isAuthenticated) { setLoading(false); return; }

    const token = localStorage.getItem('auth_token') || localStorage.getItem('auth_token');
    if (!token) { setLoading(false); return; }

    fetch(`/api/shares/view/${shareToken}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(r.status === 410 ? 'This share link has expired' : 'Failed to load content');
        return r.json();
      })
      .then(data => {
        setContent(data);
        // Update page title
        if (data.title) document.title = `${data.title} — Shared Content`;
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [isAuthenticated, authLoading, validation, shareToken]);

  const contentTypeLabel: Record<string, string> = {
    content_writer: 'Article',
    cppages: 'Content Page',
    cpcustom: 'Custom Content Page',
    briefcontent: 'Brief Content',
    servicearea: 'Service Area Page',
  };

  // Invalid or expired share
  if (validation && !validation.valid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {validation.reason === 'expired' ? 'Link Expired' : 'Link Not Found'}
          </h1>
          <p className="text-gray-500">
            {validation.reason === 'expired'
              ? 'This share link has expired. Ask the owner to create a new one.'
              : 'This share link doesn\'t exist or has been revoked.'}
          </p>
        </div>
      </div>
    );
  }

  // Not authenticated — show login prompt
  if (!authLoading && !isAuthenticated && validation?.valid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Login Required</h1>
          <p className="text-gray-500 mb-1">
            <strong>{validation.shared_by}</strong> shared "{validation.content_title}" with you.
          </p>
          <p className="text-gray-400 text-sm mb-6">You need an account to view shared content.</p>
          <button
            onClick={() => navigate(`/login?redirect=/share/${shareToken}`)}
            className="w-full bg-blue-600 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Log In to View
          </button>
        </div>
      </div>
    );
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading shared content...
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  // Content loaded
  if (!content) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
              {contentTypeLabel[content.content_type] || 'Shared'}
            </span>
            <span className="text-sm text-gray-500">
              Shared by <strong>{content.shared_by}</strong>
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm text-gray-400">
            {content.word_count > 0 && <span>{content.word_count.toLocaleString()} words</span>}
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">View Only</span>
          </div>
        </div>
      </div>

      {/* Article content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <article className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">{content.title}</h1>
          {content.meta_description && (
            <p className="text-gray-500 italic border-l-4 border-gray-200 pl-4 mb-8">
              {content.meta_description}
            </p>
          )}
          <div
            className="prose prose-lg max-w-none
              prose-headings:text-gray-900 prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-gray-700 prose-p:leading-relaxed
              prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-gray-900
              prose-ul:my-4 prose-ol:my-4 prose-li:text-gray-700
              prose-img:rounded-lg prose-img:shadow-sm
              prose-blockquote:border-blue-300 prose-blockquote:bg-blue-50 prose-blockquote:py-1 prose-blockquote:px-4"
            dangerouslySetInnerHTML={{ __html: content.content_html }}
          />
        </article>
      </div>
    </div>
  );
}
